import React, { useState, useEffect } from "react";
import flightrightIcon from "../../assets/flightright.png";
import BookingPopup from "./BookingPopup";
import { useLocation } from "react-router-dom";
import { FaSuitcase, FaChair, FaArrowsAltH } from "react-icons/fa";
import FlippableFareCard from "../ui/FlippableFareCard";
import FlightDetailsAccordion from "./FlightDetailsAccordion";
import { computeDurationMinutes, getTimezoneAbbreviation } from "../../utils/dateUtils";
import styles from "../../styles";

interface SegmentProps {
  flightNumber: string;
  airlineName: string;
  airlineLogo: string;
  from: string;
  to: string;
  departure: string;
  arrival: string;
}

interface FareTier {
  label: string;
  price: number;
  currency: string;
  baggage: string;
  cancellation: string;
  addOns: string;
  cabinCode?: string;
  fareBasisCode?: string;
  fareType?: string;
  bookingCodes?: string[];
  scheduleDetails?: Array<{ mealCode?: string; seatsAvailable?: number }>;
  brandFeatures?: Array<{ id: number; commercialName: string }>;
  fullGroup?: any[];
}

interface RoundTripFlightCardProps {
  onward: SegmentProps;
  returnSegment: SegmentProps;
  amount: number;
  currency: string;
  baseFare: number;
  taxes: number;
  classOfService: string;
  flightData?: any;
  selectedClass: string;
  selectedOnwardFareTier?: FareTier;
  selectedReturnFareTier?: FareTier;
  disableExpansion?: boolean; // Add this prop
}

const cabinClassOptions = [
  { label: "Economy", code: "Y" },
  { label: "Premium Economy", code: "S" },
  { label: "Business", code: "C" },
  { label: "Premium Business", code: "J" },
  { label: "First class", code: "F" },
  { label: "Premium First", code: "P" },
];

function getCabinLabel(code?: string) {
  return (
    cabinClassOptions.find((c) => c.code === code)?.label || code || "Unknown"
  );
}

const RoundTripFlightCard = ({
  onward,
  returnSegment,
  amount,
  currency,
  baseFare,
  taxes,
  classOfService,
  selectedClass,
  flightData,
  selectedOnwardFareTier,
  selectedReturnFareTier,
  disableExpansion = false,
}: RoundTripFlightCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const location = useLocation();

  // New format: derive legs from unified legs[]
  const outboundLeg = flightData?.legs?.find((l: any) => l.direction === "outbound") ?? flightData?.legs?.[0];
  const inboundLeg = flightData?.legs?.find((l: any) => l.direction === "inbound");
  const onwardFlight = outboundLeg;
  const returnFlight = inboundLeg;

  // Map fareOptions → FareTier (same for both legs; use legPricing for per-leg if needed)
  const cabinClassNameToCode: Record<string, string> = {
    economy: "Y", premiumeconomy: "S", business: "C",
    premiumbusiness: "J", first: "F", premiumfirst: "P",
  };
  const mapFO = (fo: any) => {
    const rawCabinCode: string | undefined = outboundLeg?.segments?.[0]?.bookingInfo?.cabinCode ?? undefined;
    const rawCabinClass: string | undefined = fo.cabinClass ?? undefined;
    const resolvedCabinCode =
      rawCabinCode ??
      (rawCabinClass
        ? cabinClassNameToCode[rawCabinClass.toLowerCase().replace(/\s+/g, "")]
        : undefined);
    return {
      label: fo.name,
      price: fo.passengerPrices?.find((p: any) => p.type === "ADT")?.total ?? fo.price?.total ?? 0,
      currency: fo.price?.currency,
      baggage: fo.baggage?.checked?.description ?? fo.baggage?.cabin?.description ?? "",
      cancellation:
        fo.refundPolicy?.conditions ??
        (fo.refundPolicy?.refundable ? "Refundable"
          : fo.refundPolicy?.cancellationFee
            ? `Non-refundable (fee: ${fo.refundPolicy.cancellationFee.amount} ${fo.refundPolicy.cancellationFee.currency})`
            : "Non-refundable"),
      addOns: fo.amenities?.map((a: any) => a.description).join(", ") ?? "",
      cabinCode: resolvedCabinCode,
      fareBasisCode: fo.fareBasis?.code,
      fareType: fo.fareBasis?.type,
      bookingCodes: fo.bookingClasses,
      brandFeatures: fo.amenities?.map((a: any, i: number) => ({ id: i, commercialName: a.description })),
      fullGroup: fo,
    };
  };

  const onwardBrandedFares: FareTier[] = Array.isArray(flightData?.fareOptions)
    ? flightData.fareOptions.map(mapFO)
    : [];
  const returnBrandedFares: FareTier[] = onwardBrandedFares; // same tiers, per-leg price via legPricing

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onwardTier = selectedOnwardFareTier ?? onwardBrandedFares[selectedIndex];
  const returnTier = selectedReturnFareTier ?? returnBrandedFares[selectedIndex];
  useEffect(() => {
    setSelectedIndex(0);
  }, [flightData?._uniqueKey]);

  const isPassengerDetailPage = location.pathname.includes("passenger-details");
  const isBookingpopup = location.pathname.includes("booking-popup");

  const onwardSeg0 = outboundLeg?.segments?.[0];
  const returnSeg0 = inboundLeg?.segments?.[0];

  const displayedOnwardSeats = onwardSeg0?.bookingInfo?.seatsAvailable ?? "-";
  const displayedReturnSeats = returnSeg0?.bookingInfo?.seatsAvailable ?? "-";
  const displayedOnwardMeal = onwardSeg0?.bookingInfo?.meal?.code;
  const displayedReturnMeal = returnSeg0?.bookingInfo?.meal?.code;

  function formatMealCode(code?: string) {
    if (!code) return null;
    const map: Record<string, string> = {
      M: "Meal", S: "Snack", RF: "Refreshment", F: "Full meal",
      D: "Dinner", B: "Breakfast", L: "Lunch",
    };
    return map[code] || code;
  }

  const displayedOnwardBaggage = isPassengerDetailPage
    ? onwardSeg0?.bookingInfo?.baggage || ""
    : (onwardTier?.baggage ?? "");

  const displayedOnwardClass = isPassengerDetailPage
    ? (onwardSeg0?.bookingInfo?.cabinCode ?? classOfService)
    : (getCabinLabel(onwardTier?.cabinCode) ?? classOfService);

  const displayedReturnBaggage = isPassengerDetailPage
    ? returnSeg0?.bookingInfo?.baggage || ""
    : (returnTier?.baggage ?? "");

  const displayedOnwardPrice = onwardTier?.price ?? amount;
  const displayedOnwardCurrency = onwardTier?.currency ?? currency;

  function formatSabreTime(timeStr: string) {
    if (!timeStr) return "";
    // Handle ISO 8601: "2026-03-15T07:50:00+05:30"
    const isoMatch = timeStr.match(/T(\d{2}:\d{2})/);
    if (isoMatch) return isoMatch[1];
    const match = timeStr.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : timeStr;
  }
  function formatElapsedTime(minutes?: number) {
    if (!minutes || isNaN(minutes)) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  const getStopSummary = (segments: any[]) => {
    const stops = segments.length - 1;
    if (stops <= 0) return "Non-stop";
    const stopCodes = segments
      .slice(0, -1)
      .map((seg: any) => seg.arrival?.airport?.code ?? seg.arrival?.airport)
      .filter(Boolean);
    return `${stops} stop${stops > 1 ? "s" : ""} (${stopCodes.join(", ")})`;
  };

  return (
    <>
      {showBookingPopup && (
        <BookingPopup
          flightData={flightData}
          onClose={() => setShowBookingPopup(false)}
          passengerCounts={location.state?.passengerCounts}
          departureDate={location.state?.departureDate}
          returnDate={location.state?.returnDate}
          selectedClass={selectedClass}
          classOfService={classOfService}
          selectedOnwardFareTier={onwardTier.fullGroup ?? [onwardTier]} // <-- pass full group for booking
          selectedReturnFareTier={returnTier.fullGroup ?? [returnTier]}
        />
      )}

      <div
        className={`bg-white w-full rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${isExpanded ? "pb-2" : ""}`}
      >
        <div
          className={`px-4 py-4 sm:px-5 ${!isPassengerDetailPage && !disableExpansion ? "cursor-pointer" : ""}`}
          onClick={() => {
            if (!isPassengerDetailPage && !disableExpansion) {
              if (isExpanded) setSelectedIndex(0);
              setIsExpanded((prev) => !prev);
            }
          }}
        >
          <div className="flex items-stretch gap-3 sm:gap-4">
            {/* Legs column */}
            <div className="flex-1 flex flex-col gap-3 min-w-0">
              {/* Outbound row */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex flex-col items-center gap-0.5 w-14 sm:w-16 shrink-0">
                  <img
                    src={onward.airlineLogo}
                    alt={onward.airlineName}
                    className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      if (!img.src.endsWith("/assets/airlines/default.png")) {
                        img.src = "/assets/airlines/default.png";
                      }
                    }}
                  />
                  <div className="text-[9px] sm:text-[10px] text-gray-500 text-center leading-tight line-clamp-1">
                    {onward.airlineName}
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 sm:gap-3">
                  <div className="shrink-0">
                    <div className="text-base sm:text-xl font-bold text-gray-900 tabular-nums leading-none">
                      {formatSabreTime(outboundLeg?.departure?.dateTime)}
                      {(() => {
                        const tz = getTimezoneAbbreviation(outboundLeg?.departure?.dateTime);
                        return tz ? (
                          <span className="ml-1 text-[9px] font-normal text-gray-400">{tz}</span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 mt-0.5">
                      {outboundLeg?.departure?.airport?.code ?? "-"}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col items-center min-w-0">
                    <div className="text-[9px] sm:text-[10px] text-gray-400 mb-0.5">
                      {formatElapsedTime(
                        outboundLeg?.duration ||
                          computeDurationMinutes(
                            outboundLeg?.departure?.dateTime,
                            outboundLeg?.arrival?.dateTime,
                            outboundLeg?.arrival?.dateAdjustment ?? 0,
                          ),
                      )}
                    </div>
                    <div className="w-full flex items-center">
                      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                      <div className="mx-1 text-[9px] text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap font-medium">
                        {getStopSummary(outboundLeg?.segments ?? [])}
                      </div>
                      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-base sm:text-xl font-bold text-gray-900 tabular-nums leading-none">
                      {formatSabreTime(outboundLeg?.arrival?.dateTime)}
                      {(outboundLeg?.arrival?.dateAdjustment ?? 0) > 0 && (
                        <span className="text-[9px] text-orange-500 align-super ml-0.5">+1</span>
                      )}
                      {(() => {
                        const tz = getTimezoneAbbreviation(outboundLeg?.arrival?.dateTime);
                        return tz ? (
                          <span className="ml-1 text-[9px] font-normal text-gray-400">{tz}</span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 mt-0.5">
                      {outboundLeg?.arrival?.airport?.code ?? "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider between legs */}
              <div className="border-t border-dashed border-gray-200 ml-14 sm:ml-16" />

              {/* Return row */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex flex-col items-center gap-0.5 w-14 sm:w-16 shrink-0">
                  <img
                    src={returnSegment.airlineLogo}
                    alt={returnSegment.airlineName}
                    className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      if (!img.src.endsWith("/assets/airlines/default.png")) {
                        img.src = "/assets/airlines/default.png";
                      }
                    }}
                  />
                  <div className="text-[9px] sm:text-[10px] text-gray-500 text-center leading-tight line-clamp-1">
                    {returnSegment.airlineName}
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 sm:gap-3">
                  <div className="shrink-0">
                    <div className="text-base sm:text-xl font-bold text-gray-900 tabular-nums leading-none">
                      {formatSabreTime(inboundLeg?.departure?.dateTime)}
                      {(() => {
                        const tz = getTimezoneAbbreviation(inboundLeg?.departure?.dateTime);
                        return tz ? (
                          <span className="ml-1 text-[9px] font-normal text-gray-400">{tz}</span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 mt-0.5">
                      {inboundLeg?.departure?.airport?.code ?? "-"}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col items-center min-w-0">
                    <div className="text-[9px] sm:text-[10px] text-gray-400 mb-0.5">
                      {formatElapsedTime(
                        inboundLeg?.duration ||
                          computeDurationMinutes(
                            inboundLeg?.departure?.dateTime,
                            inboundLeg?.arrival?.dateTime,
                            inboundLeg?.arrival?.dateAdjustment ?? 0,
                          ),
                      )}
                    </div>
                    <div className="w-full flex items-center">
                      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                      <div className="mx-1 text-[9px] text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap font-medium">
                        {getStopSummary(inboundLeg?.segments ?? [])}
                      </div>
                      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-base sm:text-xl font-bold text-gray-900 tabular-nums leading-none">
                      {formatSabreTime(inboundLeg?.arrival?.dateTime)}
                      {(inboundLeg?.arrival?.dateAdjustment ?? 0) > 0 && (
                        <span className="text-[9px] text-orange-500 align-super ml-0.5">+1</span>
                      )}
                      {(() => {
                        const tz = getTimezoneAbbreviation(inboundLeg?.arrival?.dateTime);
                        return tz ? (
                          <span className="ml-1 text-[9px] font-normal text-gray-400">{tz}</span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 mt-0.5">
                      {inboundLeg?.arrival?.airport?.code ?? "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical divider */}
            <div className="hidden sm:block self-stretch w-px bg-gray-200 shrink-0" />

            {/* Price + CTA */}
            <div className="flex flex-col items-end justify-center shrink-0 w-24 sm:w-36 pl-1 sm:pl-3">
              <div className="text-[10px] text-gray-400">per adult</div>
              <div className="text-lg sm:text-2xl font-bold text-primary leading-tight tabular-nums">
                {displayedOnwardCurrency} {displayedOnwardPrice?.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-400 mb-1">incl. taxes</div>
              <div className="text-[10px] text-gray-500 mb-1">{displayedOnwardClass}</div>
              {displayedOnwardBaggage && (
                <div className="flex items-center gap-1 text-[10px] text-gray-500">
                  <FaSuitcase size={9} />
                  <span className="truncate max-w-[80px]">{displayedOnwardBaggage}</span>
                </div>
              )}
              {!isPassengerDetailPage && !disableExpansion && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isExpanded) setSelectedIndex(0);
                    setIsExpanded((prev) => !prev);
                  }}
                  className="mt-2 bg-primary text-white text-[10px] sm:text-xs font-bold px-3 py-1.5 rounded hover:bg-blue-800 transition-colors w-full text-center"
                >
                  {isExpanded ? "HIDE" : "VIEW PRICES"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Fare Tiers Selection - Expanded Section */}
        {!isPassengerDetailPage &&
          !isBookingpopup &&
          !disableExpansion &&
          isExpanded && (
            <div className="border-t border-gray-100 bg-gray-50 px-4 py-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-bold text-primary text-sm">Select Fare</h3>
              </div>
              <div
                className={`p-4 gap-4 ${onwardBrandedFares.length <= 3
                  ? "flex justify-center items-stretch"
                  : onwardBrandedFares.length === 4
                    ? "grid grid-cols-2 items-stretch"
                    : "grid grid-cols-3 items-stretch"
                  }`}
              >
                {onwardBrandedFares.map((onwardTier, idx) => (
                  <div
                    key={`fare-${idx}`}
                    className={`${selectedIndex === idx
                      ? "border-2 border-white shadow-lg"
                      : "border border-gray-200"
                      } rounded-xl overflow-hidden ${onwardBrandedFares.length <= 3 ? "flex-1 max-w-xs" : ""
                      }`}
                  >
                    <FlippableFareCard
                      fare={onwardTier}
                      brandFeatures={onwardTier.brandFeatures ?? []}
                      brandName={onwardTier.label}
                      selected={selectedIndex === idx}
                      onClick={() => setSelectedIndex(idx)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        {/* Book Now Button */}
        {!isPassengerDetailPage && !disableExpansion && isExpanded && (
          <div className="bg-gray-50 border-t border-gray-100 px-4 py-4 flex justify-end">
            <button
              onClick={() => setShowBookingPopup(true)}
              className="bg-primary text-white font-bold px-8 py-2.5 rounded hover:bg-blue-800 transition-colors"
            >
              Book Now
            </button>
          </div>
        )}

        {/* View Flight Details accordion */}
        {!isPassengerDetailPage && !disableExpansion && (
          <FlightDetailsAccordion
            flightData={flightData}
            tier={onwardTier as any}
            baseDepartureDate={location.state?.departureDate}
            fallbackBaggage={displayedOnwardBaggage}
            fallbackCurrency={currency}
            fallbackBaseFare={baseFare}
            fallbackTaxes={taxes}
            fallbackTotal={amount}
          />
        )}
      </div>
    </>
  );
};

export default RoundTripFlightCard;
